import type { Sort } from "@store/utils/queryStateUtils";
import {
  format,
  formatDistanceToNow,
  isBefore,
  parseISO,
  subDays,
} from "date-fns";

import type { BaseDataShape } from "../../../data/types";
import { fields } from "../data/fields";

type FileKind = "file" | "directory" | "symlink";

export enum FileType {
  FOLDER = "FOLDER",
  IMAGE = "IMAGE",
  IMAGE_TIFF = "IMAGE_TIFF",
  TEXT = "TEXT",
  TABULAR = "TABULAR",
  TRASH = "TRASH",
  VIDEO = "VIDEO",
  AUDIO = "AUDIO",
  UNKNOWN = "UNKNOWN",
}

export type ParsedData = {
  name: string;
  filepath: string;
  extension: string;
  type: FileType;
  children: ParsedData;
}[];

export interface ApiFile {
  name: string;
  status: string;
  size: number;
  created: string;
  modified: string;
  kind: FileKind;
}

export interface ClientFile extends ApiFile {
  type: FileType;
  extension: string;
}

export function getExtension(path: string) {
  return path.split(".").pop()?.toLowerCase() || "";
}

export function getFileType(extension: string, kind: FileKind): FileType {
  if (kind === "directory") {
    return FileType.FOLDER;
  }
  switch (extension) {
    case "jpg":
    case "jpeg":
    case "jfif":
    case "pjpeg":
    case "pjp":
    case "png":
    case "gif":
    case "bmp":
    case "apng":
    case "ico":
    case "cur":
    case "svg":
    case "webp":
      return FileType.IMAGE;
    case "tif":
    case "tiff":
      return FileType.IMAGE_TIFF;
    case "md":
    case "txt":
    case "html":
      return FileType.TEXT;
    case "yaml":
    case "yml":
    case "tsv":
    case "xml":
    case "csv":
    case "json":
    case "jsonl":
      return FileType.TABULAR;
    case "mp4":
    case "ogv":
    case "webm":
      return FileType.VIDEO;
    case "mp3":
    case "ogg":
    case "wav":
    case "flac":
    case "m4a":
      return FileType.AUDIO;
    case "trash":
      return FileType.TRASH;
    case "pdf":
    default:
      return FileType.UNKNOWN;
  }
}

export const EXCLUDE_EXTENSIONS = ["model", "zip", "pdf"];

class MultiPartParser {
  private boundary: string;
  private leftoverString: string;
  private state: "PREAMBLE" | "HEADERS" | { fieldName: string | null };

  constructor(contentType: string) {
    // Examples for content types:
    //      multipart/form-data; boundary="----7dd322351017c"; ...
    //      multipart/form-data; boundary=----7dd322351017c; ...
    var m = contentType.match(/boundary=(?:"([^"]+)"|([^;]+))/i);
    if (!m) {
      throw new Error("Bad content-type header, no multipart boundary");
    }
    // \r\n is part of the boundary.
    this.boundary = "\r\n--" + (m[1] || m[2]);
    // Prepend what has been stripped by the body parsing mechanism.
    this.leftoverString = "\r\n";
    this.state = "PREAMBLE";
  }

  processChunk(chunk: string) {
    let contentLeft = this.leftoverString + chunk;
    let canContinue = true;
    const updates: (
      | { fileChunk: string; fieldName: string; done: false }
      | { done: true }
    )[] = [];
    while (contentLeft && canContinue) {
      switch (this.state) {
        case "PREAMBLE": {
          const boundaryIndex = contentLeft.indexOf(this.boundary);
          if (boundaryIndex === -1) {
            canContinue = false;
          } else {
            this.state = "HEADERS";
            contentLeft = contentLeft.slice(
              boundaryIndex + this.boundary.length,
            );
          }
          break;
        }
        case "HEADERS": {
          // If we encounter "--" immediately after the boundary, it means there
          // isn't a next file and we're done.
          if (contentLeft.startsWith("--")) {
            updates.push({ done: true });
            contentLeft = "";
          } else {
            // Otherwise we can assume there are headers to read.
            const [headerContent, ...after] = contentLeft.split("\r\n\r\n");
            // if no section delineator was found, we should wait for the
            // headers to be finished streaming before processing them.
            if (!after.length) {
              canContinue = false;
            } else {
              const headers = headerContent.split("\r\n").slice(1);
              let fieldName: string | undefined;
              for (const header of headers) {
                const [, name] = header.match(/^.*name="([^"]*)"$/) || [];
                if (name) {
                  fieldName = name;
                }
              }
              this.state = {
                fieldName: fieldName || null,
              };
              contentLeft = after.join("\r\n\r\n");
            }
          }
          break;
        }
        default: {
          const boundaryIndex = contentLeft.indexOf(this.boundary);
          if (boundaryIndex === -1) {
            // check if the end of our content matches the beginning of a
            // possible boundary
            let boundarySubstring = this.boundary;
            do {
              boundarySubstring = boundarySubstring.slice(0, -1);
            } while (
              boundarySubstring &&
              !contentLeft.endsWith(boundarySubstring)
            );
            if (boundarySubstring) {
              updates.push({
                fileChunk: contentLeft.slice(
                  0,
                  contentLeft.length - boundarySubstring.length,
                ),
                fieldName: this.state.fieldName ?? "",
                done: false,
              });
              contentLeft = boundarySubstring;
              canContinue = false;
            } else {
              updates.push({
                fileChunk: contentLeft,
                fieldName: this.state.fieldName ?? "",
                done: false,
              });
              contentLeft = "";
            }
          } else {
            updates.push({
              fileChunk: contentLeft.slice(0, boundaryIndex),
              fieldName: this.state.fieldName ?? "",
              done: false,
            });
            this.state = "HEADERS";
            contentLeft = contentLeft.slice(
              boundaryIndex + this.boundary.length,
            );
          }
          break;
        }
      }
    }
    this.leftoverString = contentLeft;
    return updates;
  }
}

export function multiPartParseStream(response: Response) {
  const contentType = response.headers.get("Content-Type");
  if (!contentType) {
    throw new Error("Expected Content-Type header");
  }
  const parser = new MultiPartParser(contentType);
  // Clone response to avoid ReadableStream conflict with OpenReplay tracker
  const clonedResponse = response.clone();
  if (!clonedResponse.body) {
    throw new Error("Expected body stream");
  }
  const bodyStream = clonedResponse.body;
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder("utf-8");
  let cancelled = false;
  let lastPullPromise:
    | Promise<ReadableStreamReadResult<Uint8Array>>
    | undefined;
  return new ReadableStream<{ fieldName: string; fileChunk: string }>({
    async pull(controller) {
      if (cancelled) {
        return;
      }
      lastPullPromise = reader.read();
      const { done, value } = await lastPullPromise;
      if (cancelled) {
        return;
      }
      if (done) {
        controller.close();
        return;
      }
      for (const update of parser.processChunk(decoder.decode(value))) {
        if (update.done) {
          controller.close();
          break;
        }
        controller.enqueue(update);
      }
    },
    async cancel(reason) {
      cancelled = true;
      await Promise.resolve(lastPullPromise);
      reader.releaseLock();
      bodyStream.cancel(reason);
    },
  });
}

export function formatDate(isoDate: string) {
  const date = parseISO(isoDate);
  if (isBefore(date, subDays(new Date(), 1))) {
    return format(date, "yyyy-MM-dd, hh:mm a");
  } else {
    return `${formatDistanceToNow(date)} ago`;
  }
}

export const toggleSortDirection = (
  currentDirection: "asc" | "desc",
): "asc" | "desc" => {
  return currentDirection === "asc" ? "desc" : "asc";
};

export type QueryableDataShape = BaseDataShape & {
  created: string;
};

export const DefaultFieldSort: Sort<QueryableDataShape> = {
  fieldName: fields.created.fieldName,
  direction: "desc",
};
